import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../config/axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { 
  MdLightMode, 
  MdDarkMode,
  MdLanguage,
  MdExpandMore,
  MdPerson,
  MdLogout,
  MdCheck,
  // MdSettings, // Not used
  MdClose,
  MdSave
} from 'react-icons/md';
import ertinuLogo from '../../assets/images/ertinu_logo.png';
import { useLanguage } from '../../context/LanguageContext';
import { BASE_URL } from '../../config/environment';

// Logo URL helper fonksiyonu
const getLogoUrl = (logoPath) => {
  if (!logoPath) return null;
  // Eğer logoPath zaten tam URL ise (http ile başlıyorsa) direkt döndür
  if (logoPath.startsWith('http')) {
    return logoPath;
  }
  // Eğer relative path ise BASE_URL ile birleştir
  return `${BASE_URL}${logoPath}`;
};

const CustomerHeader = () => {
  const { t, currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const [user, setUser] = useState({
    full_name: '',
    initials: '',
    email: '',
    phone: ''
  });

  const [companyLogo, setCompanyLogo] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const userMenuRef = useRef(null);

  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false);
  const [accountForm, setAccountForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Dark mode localStorage'dan yükle
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('isDarkMode');
    if (savedDarkMode) {
      const darkModeValue = JSON.parse(savedDarkMode);
      setIsDarkMode(darkModeValue);
      
      // HTML element'ine dark class ekle/çıkar
      if (darkModeValue) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      const fullName = userData.full_name || '';
      const initials = fullName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase();
      
      const currentUser = {
        ...userData,
        full_name: fullName,
        initials: initials,
        email: userData.user_email || '',
        phone: userData.user_gsm || ''
      };

      setUser(currentUser);
      
      // Set company logo if available
      if (userData.company_logo) {
        setCompanyLogo(userData.company_logo);
      }
      
      setAccountForm({
        full_name: fullName,
        email: userData.user_email || '',
        phone: userData.user_gsm || '',
        new_password: '',
        confirm_password: ''
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    
    // localStorage'a kaydet
    localStorage.setItem('isDarkMode', JSON.stringify(newDarkMode));
    
    // HTML element'ine dark class ekle/çıkar
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const languages = [
    { code: 'tr', label: 'Türkçe' },
    { code: 'en', label: 'English' }
  ];

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsLanguageOpen(false);
  };
  
  const openAccountSettings = () => {
    setIsAccountSettingsOpen(true);
    setIsUserMenuOpen(false);
    setFormErrors({});
    setSuccessMessage('');
  };
  
  const closeAccountSettings = () => setIsAccountSettingsOpen(false);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };
  
  const validateForm = () => {
    const errors = {};
    if (!accountForm.full_name.trim()) errors.full_name = 'İsim Soyisim alanı zorunludur';
    if (!accountForm.email.trim()) {
      errors.email = 'E-posta alanı zorunludur';
    } else if (!/\S+@\S+\.\S+/.test(accountForm.email)) {
      errors.email = 'Geçerli bir e-posta adresi giriniz';
    }
    if (accountForm.phone && !/^[0-9+() -]{10,15}$/.test(accountForm.phone)) {
      errors.phone = 'Geçerli bir telefon numarası giriniz';
    }
    // Şifre alanları opsiyonel - sadece doldurulmuşsa kontrol et
    if (accountForm.new_password || accountForm.confirm_password) {
      if (accountForm.new_password && accountForm.new_password.length < 6) {
        errors.new_password = 'Şifre en az 6 karakter olmalıdır';
      }
      if (accountForm.new_password && accountForm.new_password !== accountForm.confirm_password) {
        errors.confirm_password = 'Şifreler eşleşmiyor';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSuccessMessage('');
    
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const updateData = {
        full_name: accountForm.full_name,
        user_email: accountForm.email,
        user_gsm: accountForm.phone,
      };
      
      if (accountForm.new_password) {
        updateData.password = accountForm.new_password;
        // You might need to send current_password for verification on backend
        // updateData.current_password = accountForm.current_password;
      }
      
      const response = await api.put(
        `/customer-users/${userData.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.status === 200) {
        const updatedUserData = {
          ...userData,
          full_name: accountForm.full_name,
          user_email: accountForm.email,
          user_gsm: accountForm.phone,
        };
        localStorage.setItem('user', JSON.stringify(updatedUserData));
        
        setUser(prev => ({
          ...prev,
          full_name: accountForm.full_name,
          initials: accountForm.full_name.split(' ').map(n => n[0]).join('').toUpperCase()
        }));
        
        setSuccessMessage('Hesap bilgileriniz başarıyla güncellendi');
        setAccountForm(prev => ({
          ...prev,
          new_password: '',
          confirm_password: ''
        }));
      }
    } catch (error) {
      console.error('Error updating user information:', error);
      setFormErrors(prev => ({
        ...prev,
        general: error.response?.data?.message || 'Bilgileriniz güncellenirken bir hata oluştu.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <header className="bg-[#272c39] dark:bg-gray-800 text-white p-4 shadow-md sticky top-0 z-40 transition-colors duration-300">
        <div className="container mx-auto flex justify-between items-center">
          <div 
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity ml-2.5"
            onClick={() => navigate('/customer-dashboard')}
            title="Dashboard'a Git"
          >
            <img 
              src={getLogoUrl(companyLogo) || ertinuLogo} 
              alt={companyLogo ? "Company Logo" : "ERTINU Logo"} 
              className="h-16 mr-3 object-contain" 
              onError={(e) => {
                // Logo yüklenemezse varsayılan logoyu göster
                e.target.src = ertinuLogo;
                e.target.alt = "ERTINU Logo";
              }}
            />
          </div>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={toggleDarkMode}
              className="p-1.5 hover:bg-white/10 rounded-full"
              title={isDarkMode ? 'Gündüz Moduna Geç' : 'Gece Moduna Geç'}
            >
              {isDarkMode ? <MdLightMode size={20} className="text-white/90" /> : <MdDarkMode size={20} className="text-white/90" />}
            </button>
            
            <div className="relative" ref={userMenuRef}>
              <button 
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-3 hover:bg-white/10 rounded-md px-2 py-1.5"
              >
                <span className="text-sm text-white/90">{user.full_name || 'Müşteri'}</span>
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                  <span className="text-sm font-medium">{user.initials || 'M'}</span>
                </div>
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-1 bg-[#2f354a] dark:bg-gray-700 rounded-md shadow-lg py-1 min-w-[200px] z-50">
                  <button
                    onClick={openAccountSettings}
                    className="w-full text-left px-4 py-2 text-sm text-white/90 dark:text-white hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center gap-2"
                  >
                    <MdPerson size={18} />
                    {t('customerHeader.accountSettings')}
                  </button>

                  <button
                    onClick={() => setIsUserMenuOpen(false)}
                    className="w-full text-left px-4 py-2 text-sm text-white/90 dark:text-white hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center gap-2"
                  >
                    
                  </button>

                  <div className="relative">
                    <button
                      onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                      className="w-full text-left px-4 py-2 text-sm text-white/90 dark:text-white hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2"><MdLanguage size={18} />{t('common.language')}</div>
                      <div className="flex items-center gap-1">
                        <span>{currentLanguage.toUpperCase()}</span>
                        <MdExpandMore className={`transition-transform duration-200 ${isLanguageOpen ? 'rotate-180' : ''}`} size={18} />
                      </div>
                    </button>
                    {isLanguageOpen && (
                      <div className="absolute right-0 mt-1 bg-[#2f354a] dark:bg-gray-700 rounded-md shadow-lg py-1 min-w-[160px] border border-gray-700 dark:border-gray-600 z-50">
                        {languages.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className="w-full text-left px-4 py-2 text-sm text-white/90 dark:text-white hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center justify-between"
                          >
                            {lang.label}
                            {currentLanguage === lang.code && <MdCheck size={18} className="text-blue-400" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-700 dark:border-gray-600 my-1"></div>

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-white/90 dark:text-white hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center gap-2"
                  >
                    <MdLogout size={18} />
                    {t('customerHeader.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {isAccountSettingsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-[#272c39] to-[#394254] p-4 text-white flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center"><MdPerson className="mr-2" size={20} />{t('customerHeader.accountSettings')}</h3>
              <button onClick={closeAccountSettings} className="text-white/80 hover:text-white"><MdClose size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {successMessage && <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-md">{successMessage}</div>}
              {formErrors.general && <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-md">{formErrors.general}</div>}
              
              <div className="mb-4">
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customerHeader.fullName')}</label>
                <input id="full_name" name="full_name" type="text" value={accountForm.full_name} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${formErrors.full_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`} />
                {formErrors.full_name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.full_name}</p>}
              </div>
              
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customerHeader.email')}</label>
                <input id="email" name="email" type="email" value={accountForm.email} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${formErrors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`} />
                {formErrors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.email}</p>}
              </div>

              <div className="mb-4">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customerHeader.phone')}</label>
                <input id="phone" name="phone" type="text" value={accountForm.phone} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${formErrors.phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`} placeholder="+90 (xxx) xxx xx xx" />
                {formErrors.phone && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.phone}</p>}
              </div>
              
              <div className="border-t border-gray-200 dark:border-gray-600 my-4 pt-4">
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-2">{t('customerHeader.changePassword')}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Şifre değiştirmek istemiyorsanız bu alanları boş bırakabilirsiniz.</p>
                
                
                <div className="mb-4">
                  <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customerHeader.newPassword')} <span className="text-gray-500 text-sm">(Opsiyonel)</span></label>
                  <div className="relative">
                    <input id="new_password" name="new_password" type={showPassword.new ? "text" : "password"} value={accountForm.new_password} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${formErrors.new_password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`} placeholder="Yeni şifre" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-400" onClick={() => togglePasswordVisibility('new')}>{showPassword.new ? <FaEyeSlash /> : <FaEye />}</button>
                  </div>
                  {formErrors.new_password && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.new_password}</p>}
                </div>
                
                <div className="mb-4">
                  <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customerHeader.confirmPassword')} <span className="text-gray-500 text-sm">(Opsiyonel)</span></label>
                  <div className="relative">
                    <input id="confirm_password" name="confirm_password" type={showPassword.confirm ? "text" : "password"} value={accountForm.confirm_password} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${formErrors.confirm_password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`} placeholder="Yeni şifre tekrarı" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-400" onClick={() => togglePasswordVisibility('confirm')}>{showPassword.confirm ? <FaEyeSlash /> : <FaEye />}</button>
                  </div>
                  {formErrors.confirm_password && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.confirm_password}</p>}
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={closeAccountSettings} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-white bg-transparent dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">{t('customerHeader.cancel')}</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1" disabled={isSubmitting}>
                  {isSubmitting ? (<><span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-1"></span>{t('customerHeader.saving')}</>) : (<><MdSave size={18} />{t('customerHeader.save')}</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerHeader;